/**
 * ``useApplicationTitle`` — the classification-specific top-bar wordmark
 * (REQ-UI-060).
 *
 * The title is per-deployment (A → "MERIDIAN | WHEELHOUSE", B →
 * "MERIDIAN | NIGHTCAP", C → "MERIDIAN | ARSENAL PARK"; ADR-042 D-4 dropped
 * the designation letter from the codename),
 * served by the BFF via the authenticated ``GET /api/config`` and read
 * here from the shared :func:`useDeploymentConfig` query (so the top bar
 * and the classification banner share a single cached fetch).
 *
 * While the (post-login) fetch is pending — and on any error — the title
 * falls back to :const:`DEFAULT_APPLICATION_TITLE` ("MERIDIAN"), so the
 * wordmark is never blank and the per-deployment suffix appears as a
 * progressive enhancement once the config resolves.
 *
 * Cross-references:
 *   - REQ-UI-060 (classification-specific title via the config service).
 *   - chrome/use-deployment-config.ts (the shared source).
 *   - chrome/top-bar.tsx (the consumer).
 */
import { useDeploymentConfig } from "@/chrome/use-deployment-config";

/** Fallback wordmark shown until the deployment config resolves. */
export const DEFAULT_APPLICATION_TITLE = "MERIDIAN";

/**
 * Resolve the application title for the top-bar wordmark.
 *
 * @returns The deployment's title, or :const:`DEFAULT_APPLICATION_TITLE`
 *     while the config is pending / on error.
 */
export function useApplicationTitle(): string {
  return useDeploymentConfig()?.title ?? DEFAULT_APPLICATION_TITLE;
}
